import styled from "styled-components";
import { Alert, Col, Row } from "antd";

import { CustomerInformationSectionTitle } from "@/v1/components/common/styledComponents";
import { foundationColors } from "@/v1/themes/colors";

const expandedRowBackground = "#fbfbfb";

export const PageHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const Title = styled.h1`
  color: ${foundationColors["base-deep-purple"]};
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

export const Filters = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  max-width: 520px;
`;

export const Section = styled.section`
  background: transparent;
  border-bottom: 1px solid ${foundationColors["deep-purple-100"]};
  margin-bottom: 20px;
  padding: 0 0 4px;

  &:last-of-type {
    border-bottom: 0;
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled(CustomerInformationSectionTitle)`
  margin-bottom: 16px;
`;

export const InfoMessage = styled.div<{ $hasExtraBottomSpacing?: boolean }>`
  background: ${foundationColors["lavender-100"]};
  border: 1px solid ${foundationColors["deep-purple-200"]};
  border-radius: 8px;
  color: ${foundationColors["base-deep-purple"]};
  margin-bottom: ${({ $hasExtraBottomSpacing }) =>
    $hasExtraBottomSpacing ? "24px" : "16px"};
  padding: 10px 12px;
`;

export const AlignedFormRow = styled(Row)<{ $labelMinHeight?: string }>`
  > .ant-col {
    display: flex;
  }

  .ant-form-item {
    width: 100%;
  }

  .ant-form-item-label {
    align-items: flex-start;
    display: flex;
    min-height: ${({ $labelMinHeight }) => $labelMinHeight || "44px"};
  }
`;

export const AlignedFormLink = styled.a`
  align-items: center;
  display: inline-flex;
  min-height: 32px;
`;

export const InlineForm = styled.div`
  background: ${expandedRowBackground};
  margin: auto;
  max-width: 900px;
  padding: 20px;
`;

export const ExpandedRowHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 auto;
  max-width: 900px;
`;

export const SubmittedDocumentsContainer = styled.div`
  margin: 0 auto 20px;
  max-width: 900px;

  h3 {
    margin-bottom: 10px;
  }
`;

export const PreviewListContainer = styled.div`
  border-bottom: 1px solid ${foundationColors["deep-purple-100"]};
  border-top: 1px solid ${foundationColors["deep-purple-100"]};

  .ant-list-header {
    background: ${foundationColors["deep-purple-100"]};
  }

  .ant-list-header,
  .ant-list-item {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const PreviewCodeCol = styled(Col)`
  padding-left: 18px;
`;

export const PreviewValueCol = styled(Col)`
  padding-right: 18px;
  text-align: right;
  word-break: break-word;
`;

/**
 * The FsE_KapG section a Zeile belongs to. The KapG preview is long enough that
 * a flat list of numbers is unreadable, and the section is what an operator
 * matches against the ELSTER form.
 */
export const PreviewSectionHeader = styled.div`
  background: ${foundationColors["lavender-100"]};
  color: ${foundationColors["base-deep-purple"]};
  font-weight: 600;
  margin: 0 -18px;
  padding: 6px 18px;
  width: calc(100% + 36px);
`;

/** One entry of a repeating block: "Gesetzlicher Vertreter 2". */
export const PreviewGroupHeader = styled.div`
  color: ${foundationColors["base-deep-purple"]};
  font-weight: 600;
  padding-left: 18px;
  width: 100%;
`;

/** What ERiC answered, above the rows it is about. */
export const PreviewErrorAlert = styled(Alert)`
  margin-bottom: 16px;
`;

/**
 * One entry of a repeating block (a gesetzlicher Vertreter, an Anteilseigner).
 * The border is what tells the operator where one person's answers end, which
 * matters most in the Anteilseigner list where six entries repeat the same
 * labels.
 */
export const RepeatingEntry = styled.div`
  border: 1px solid ${foundationColors["deep-purple-100"]};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 16px 0;
`;

export const RepeatingEntryHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  strong {
    color: ${foundationColors["base-deep-purple"]};
  }
`;

/**
 * An answer the ELSTER template fixes: shown so the operator knows what is
 * being filed, never an input, never stored.
 */
export const ReadOnlyAnswer = styled.div`
  align-items: baseline;
  border-bottom: 1px dashed ${foundationColors["deep-purple-100"]};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 8px 0;

  &:last-of-type {
    border-bottom: 0;
  }

  strong {
    color: ${foundationColors["base-deep-purple"]};
    white-space: nowrap;
  }

  small {
    display: block;
    opacity: 0.75;
  }
`;

/**
 * The Anlagen of a KapG record (Zeile 227, SMT-1341). One row per file, the
 * name on the left and the actions on the right, so a list of twenty stays
 * scannable and the file names line up.
 */
export const AttachmentUploadRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

export const AttachmentHint = styled.small`
  color: ${foundationColors["base-deep-purple"]};
  opacity: 0.75;
`;

export const AttachmentRow = styled.div`
  align-items: center;
  border-bottom: 1px dashed ${foundationColors["deep-purple-100"]};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 8px 0;

  &:last-of-type {
    border-bottom: 0;
  }
`;

export const AttachmentName = styled.span`
  overflow-wrap: anywhere;
`;

export const AttachmentSize = styled.span`
  color: ${foundationColors["base-deep-purple"]};
  margin-left: 8px;
  opacity: 0.75;
  white-space: nowrap;
`;

export const AttachmentEmpty = styled.p`
  color: ${foundationColors["base-deep-purple"]};
  margin: 0;
  opacity: 0.75;
`;

/**
 * antd 4's `Input.TextArea showCount` renders the character counter as a
 * `::after` pseudo-element on the textarea's own wrapper (`content:
 * attr(data-count)`, `float: right`), and inside a `Form.Item` it pulls that
 * counter up with `margin-bottom: -22px`
 * (`.ant-input-textarea-show-count.ant-input-textarea-in-form-item::after`).
 * That negative margin is what makes the counter take no space of its own, so
 * the `Form.Item`'s error text (`.ant-form-item-explain`) that follows starts
 * in the same strip instead of below it — the two overlap whenever a rule
 * fires on that field (SMT-1361, both the KapG and the EUN activity
 * description). Cancelling the negative margin gives the counter back its own
 * (single-line, `white-space: nowrap`, so never more than one line itself)
 * row, which the explain text then starts below regardless of whether the
 * error message itself is one line or wraps to two — the fix does not depend
 * on the error's height, only on the counter no longer intruding into it.
 * Scoped to the textareas that opt in rather than applied globally, since
 * `showCount` combined with a validation rule only exists on these two.
 */
export const TextAreaCounterFix = styled.div`
  .ant-input-textarea-show-count.ant-input-textarea-in-form-item::after {
    margin-bottom: 0;
  }
`;

export const InlineFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
`;

export const TaxRegistrationTableWrapper = styled.div`
  .ant-table-expanded-row > .ant-table-cell,
  .ant-table-expanded-row-fixed {
    background: ${expandedRowBackground};
  }

  .ant-table-expanded-row:hover > .ant-table-cell,
  .ant-table-expanded-row:hover .ant-table-expanded-row-fixed {
    background: ${expandedRowBackground};
  }
`;
